import styled from '@emotion/styled';
import { TableCell, Table, TableHead, TableRow, TableBody } from '@mui/material';
import { DeliverySummary } from 'api/models/Deliveries';
import theme from 'App.theme';
import { formatDate } from 'components/DatePicker/DateInput';
import { useLanguage } from 'context/AppContext';
import { useRef, useState, useEffect, useCallback } from 'react';
import { TurnaroundPageVariant } from '../models';
import { useTurnaroundDestinationDeliveryId } from '../hooks/useTurnaroundDestinationDeliveryId';
import { useTurnaroundOriginDeliveryId } from '../hooks/useTurnaroundOriginDeliveryId';
import { useTurnaroundDestinationSelectedEntities } from '../hooks/useTurnaroundDestinationSelectedEntities';
import { useTurnaroundDestinationEntities } from '../hooks/useTurnaroundDestinationEntities';
import { useTurnaroundOriginSelectedEntities } from '../hooks/useTurnaroundOriginSelectedEntities';
import { trackEvent } from 'analytics';
import { EventType } from 'analytics/models';
import { useCurrentUser } from 'api/hooks/useCurrentUser';

const StyledTableCell = styled(TableCell)({
  padding: `${theme.layout.spacing.small}`,
  fontSize: 12,
  lineHeight: '22px',
  color: 'inherit',

  // truncate text
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const StyledHeaderTableCell = styled(StyledTableCell)({
  backgroundColor: theme.greys.GREY0,
  fontWeight: 500,
});

type Props = {
  variant: TurnaroundPageVariant;
  deliveries: DeliverySummary[];
  hasSuccess: boolean;
};

export const TurnaroundSurgeriesList = ({ variant, deliveries, hasSuccess }: Props) => {
  const lang = useLanguage()[0].TurnaroundSearchSurgeries;

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState<number | null>(null);

  const { data: currentUser } = useCurrentUser();

  const [turnaroundOriginDeliveryId, setTurnaroundOriginDeliveryId] = useTurnaroundOriginDeliveryId();
  const [turnaroundDestinationDeliveryId, setTurnaroundDestinationDeliveryId] = useTurnaroundDestinationDeliveryId();
  const setTurnaroundDestinationEntities = useTurnaroundDestinationEntities()[1];
  const setTurnaroundOriginSelectedEntities = useTurnaroundOriginSelectedEntities()[1];
  const setTurnaroundDestinationSelectedEntities = useTurnaroundDestinationSelectedEntities()[1];

  // Measure the height of the table and set the height of the container so that it can overflow
  useEffect(() => {
    if (tableContainerRef.current) {
      setTableHeight(tableContainerRef.current.clientHeight);
    }
  }, []);

  const onSelectDelivery = useCallback(
    async (delivery: DeliverySummary) => {
      if (variant === 'origin') {
        // Check if the delivery is already selected, if so, deselect it
        if (delivery.deliveryId === turnaroundOriginDeliveryId) {
          setTurnaroundOriginDeliveryId(null);
        } else {
          setTurnaroundOriginDeliveryId(delivery.deliveryId);
        }
      } else {
        if (delivery.deliveryId === turnaroundDestinationDeliveryId) {
          setTurnaroundDestinationDeliveryId(null);
        } else {
          setTurnaroundDestinationDeliveryId(delivery.deliveryId);
        }
      }

      // Reset the selected sets state when a delivery changes
      if (variant === 'origin') {
        setTurnaroundDestinationEntities([]);
        setTurnaroundOriginSelectedEntities([]);
        setTurnaroundDestinationSelectedEntities([]);
      }

      // Analytics
      if (!currentUser) {
        throw new Error('Current user is not defined');
      }

      await trackEvent(
        variant === 'origin' ? EventType.TurnaroundSelectSurgeryOrigin : EventType.TurnaroundSelectSurgeryDestination,
        {
          current_user: currentUser.userId,
          tenant_name: currentUser.tenantName,
          supplier: delivery.supplier || '',
          order_number: delivery.orderNumber || '',
          delivery_number: delivery.deliveryNumber || '',
          surgeon: delivery.surgeon || '',
          surgery_date: delivery.surgeryDate.toString() || '',
          surgery_reference: delivery.surgeryReference || '',
        },
      );
    },
    [
      variant,
      turnaroundOriginDeliveryId,
      turnaroundDestinationDeliveryId,
      setTurnaroundDestinationEntities,
      setTurnaroundOriginSelectedEntities,
      setTurnaroundDestinationSelectedEntities,
      currentUser,
      setTurnaroundOriginDeliveryId,
      setTurnaroundDestinationDeliveryId,
    ],
  );

  return (
    <div
      ref={tableContainerRef}
      style={{
        marginTop: theme.layout.spacing.tiny,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 100,
        maxHeight: tableHeight || 'auto',
        overflowY: 'auto',
      }}
    >
      <Table
        stickyHeader
        sx={{
          border: `1px solid ${theme.greys.GREYA2}`,
          backgroundColor: theme.colors.white,
          tableLayout: 'fixed',
        }}
      >
        <TableHead>
          <TableRow>
            <StyledHeaderTableCell>{lang.tableHeaders.surgeryReference}</StyledHeaderTableCell>
            <StyledHeaderTableCell>{lang.tableHeaders.surgeon}</StyledHeaderTableCell>
            <StyledHeaderTableCell>{lang.tableHeaders.surgeryDate}</StyledHeaderTableCell>
            <StyledHeaderTableCell>{lang.tableHeaders.deliveryNumber}</StyledHeaderTableCell>
            <StyledHeaderTableCell>{lang.tableHeaders.patientInitials}</StyledHeaderTableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {
            // Blank state
            !deliveries?.length ? (
              <TableRow>
                <StyledTableCell colSpan={3} align="center">
                  {hasSuccess ? lang.noSurgeries : lang.pleaseSearch}
                </StyledTableCell>
              </TableRow>
            ) : (
              deliveries?.map(delivery => {
                const isSelected =
                  variant === 'origin'
                    ? delivery.deliveryId === turnaroundOriginDeliveryId
                    : delivery.deliveryId === turnaroundDestinationDeliveryId;
                const isDeliveryCancelled = delivery.status === 'CANCELED';

                return (
                  <TableRow
                    key={delivery.deliveryId}
                    selected={isSelected}
                    onClick={() => onSelectDelivery(delivery)}
                    sx={{
                      height: 40,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      color: isDeliveryCancelled ? theme.colors.red : theme.colors.black,
                      '&:hover': {
                        backgroundColor: theme.greys.GREYF5,
                      },
                      '&.MuiTableRow-root.Mui-selected': {
                        backgroundColor: variant === 'origin' ? theme.colors.primaryMedium : theme.colors.amberMedium,
                      },
                    }}
                  >
                    <StyledTableCell>{delivery.surgeryReference}</StyledTableCell>
                    <StyledTableCell>{delivery.surgeon}</StyledTableCell>
                    <StyledTableCell>{formatDate(delivery.surgeryDate.toString())}</StyledTableCell>
                    <StyledTableCell>{delivery.deliveryNumber}</StyledTableCell>
                    <StyledTableCell>{delivery.patientInitials}</StyledTableCell>
                  </TableRow>
                );
              })
            )
          }
        </TableBody>
      </Table>
    </div>
  );
};
